import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import PeriodoSelector, { periodoInicial, rangoDePeriodo } from "@/components/PeriodoSelector";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Award, TrendingUp, ShoppingCart, DollarSign, Eye, EyeOff, ArrowRight,
  CheckCircle2, Clock, Lock, Wallet, FileDown,
} from "lucide-react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import UsageTip from "@/components/UsageTip";
import DescargaFormatoModal from "@/components/DescargaFormatoModal";
import { toast } from "sonner";

/**
 * Mis Comisiones — vista privada del vendedor con el detalle de sus comisiones.
 * Regla del negocio (Feb 2026): la comisión NO se paga al vendedor hasta que
 * el cliente haya saldado el 100% del pedido. Por eso mostramos 3 estados:
 *   💰 Pagadas       — director ya te pagó
 *   ⏳ Por comisionar    — cliente pagó 100%, comisión lista
 *   🔒 Bloqueadas    — cliente aún debe; comisión no se paga aún
 */


const ESTADO_COMISION_TABS = [
  { k: "todos", label: "Todas", icon: Award, color: "muted" },
  { k: "liquidada", label: "Pagadas", icon: CheckCircle2, color: "emerald" },
  { k: "pendiente_liquidacion", label: "Por comisionar", icon: Clock, color: "amber" },
  { k: "pendiente_pago_cliente", label: "Bloqueadas", icon: Lock, color: "rose" },
];

const ESTADO_COMISION_META = {
  liquidada: {
    label: "Pagada",
    color: "bg-emerald-500/15 text-emerald-700 border-emerald-500/40",
    icon: CheckCircle2,
    ayuda: "El director ya te liquidó esta comisión.",
  },
  pendiente_liquidacion: {
    label: "Por comisionar",
    color: "bg-amber-500/15 text-amber-800 border-amber-500/40",
    icon: Clock,
    ayuda: "El cliente pagó 100%. Esta comisión ya está lista para que el director te la pague.",
  },
  pendiente_pago_cliente: {
    label: "Bloqueada",
    color: "bg-rose-500/15 text-rose-700 border-rose-500/40",
    icon: Lock,
    ayuda: "El cliente aún tiene saldo pendiente. Esta comisión NO se paga hasta que la cuenta quede saldada.",
  },
};


const formatCOP = (n) => `$${Math.round(n || 0).toLocaleString("es-CO")}`;

export default function MisComisionesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchHighlight = searchParams.get("batch"); // llegó desde una notif "Comisión pagada"
  const [periodo, setPeriodo] = useState(periodoInicial);
  const [showMontos, setShowMontos] = useState(true);
  const [vendedorId, setVendedorId] = useState("");
  const [vendedores, setVendedores] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [estadoComision, setEstadoComision] = useState(batchHighlight ? "liquidada" : "todos");
  const [facturaBatch, setFacturaBatch] = useState(null); // batch_id para el selector de formato

  // Si llega con ?batch=X (desde la notificación "Comisión pagada"),
  // NO auto-descargar el PDF (eso desconecta al usuario de la vista).
  // Solo mostrar el aviso arriba con los botones para descargar factura/comprobante.
  // El usuario decide qué hacer con los botones que ya existen en cada fila.
  useEffect(() => {
    if (!batchHighlight || isAdmin) return;
    toast.success("Aquí puedes ver y descargar la factura de tu pago de comisiones ✨", { duration: 5000 });
    // Nota: NO limpiamos el param inmediatamente para que el filtro se mantenga
    // aplicado; se limpia cuando el usuario cambia de filtro.
    // eslint-disable-next-line
  }, [batchHighlight, isAdmin]);

  // Cargar lista de vendedores (solo admin, pero admin va a ser redirigido)
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/team").then(({ data }) => setVendedores((data || []).filter((u) => u.role !== "admin"))).catch(() => {});
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const r = rangoDePeriodo(periodo);
      if (r.desde) params.set("desde", r.desde);
      if (r.hasta) params.set("hasta", r.hasta);
      if (isAdmin && vendedorId) params.set("vendedor_id", vendedorId);
      const { data } = await api.get(`/reportes/comisiones?${params}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (!isAdmin) load(); /* eslint-disable-next-line */ }, [periodo, vendedorId]);

  const total = data?.total || {
    pedidos: 0, total_ventas: 0, total_comision: 0,
    comision_liquidada: 0, comision_por_cobrar: 0, comision_bloqueada: 0,
  };
  const porEstado = data?.totales_por_estado || {
    liquidada: { pedidos: 0, total_comision: 0 },
    pendiente_liquidacion: { pedidos: 0, total_comision: 0 },
    pendiente_pago_cliente: { pedidos: 0, total_comision: 0 },
  };

  const detalleFiltrado = useMemo(() => {
    const items = data?.detalle || [];
    if (estadoComision === "todos") return items;
    return items.filter((it) => it.estado_comision === estadoComision);
  }, [data, estadoComision]);

  // Agrupar por pedido para no repetir header
  const agrupadoPorPedido = useMemo(() => {
    const map = new Map();
    detalleFiltrado.forEach((it) => {
      if (!map.has(it.pedido_id)) {
        map.set(it.pedido_id, {
          pedido_id: it.pedido_id,
          pedido_numero: it.pedido_numero,
          fecha: it.fecha,
          estado: it.estado,
          cliente_nombre: it.cliente_nombre,
          vendedor_nombre: it.vendedor_nombre,
          estado_comision: it.estado_comision,
          total_pedido: it.total_pedido,
          saldo_pendiente_cliente: it.saldo_pendiente_cliente,
          esta_pagado: it.esta_pagado,
          liquidada_at: it.liquidada_at,
          metodo_pago_vendedor: it.metodo_pago_vendedor,
          referencia_liquidacion: it.referencia_liquidacion,
          batch_id: it.batch_id,
          tiene_comprobante: it.tiene_comprobante,
          items: [],
          total_comision: 0,
          total_venta: 0,
        });
      }
      const g = map.get(it.pedido_id);
      g.items.push(it);
      g.total_comision += it.comision_valor || 0;
      g.total_venta += it.subtotal || 0;
    });
    return Array.from(map.values()).sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));
  }, [detalleFiltrado]);

  // El director NO tiene comisiones propias — redirigimos al panel de liquidación
  // (después de declarar TODOS los hooks para respetar las Rules of Hooks)
  if (isAdmin) return <Navigate to="/liquidacion-comisiones" replace />;

  const hidden = (val) => showMontos ? formatCOP(val) : "•••••";
  const activeTabMeta = ESTADO_COMISION_TABS.find((t) => t.k === estadoComision);

  return (
    <div>
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Vista privada</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2 flex items-center gap-3">
            <Award className="w-8 h-8 text-primary" /> {isAdmin ? "Comisiones del equipo" : "Mis comisiones"}
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            {isAdmin
              ? "Detalle de comisiones por vendedor y su estado de liquidación."
              : "Aquí ves cuánto has ganado y cuánto sigue bloqueado hasta que tus clientes paguen. Esta pantalla es solo para ti."}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Button onClick={() => setShowMontos((s) => !s)} variant="outline" className="h-11" data-testid="toggle-montos">
            {showMontos ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showMontos ? "Ocultar montos" : "Mostrar montos"}
          </Button>
        </div>
      </header>

      <UsageTip storageKey="mis-comisiones-v2" steps={[
        "La <b>comisión</b> se calcula al confirmar el pedido según la regla de cada producto.",
        "Un pedido pasa a <b>Por comisionar</b> solo cuando el cliente lo paga al <b>100%</b>. Antes queda <b>Bloqueado</b>.",
        "El director marca los pedidos como <b>Pagadas</b> desde <b>Liquidación de comisiones</b>. Ahí quedan cerrados.",
        "Presiona <b>Ocultar montos</b> si necesitas revisar la pantalla con alguien al lado.",
      ]} />

      {/* Filtros */}
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <PeriodoSelector value={periodo} onChange={setPeriodo} testid="rango" />
        {isAdmin && (
          <select
            value={vendedorId}
            onChange={(e) => setVendedorId(e.target.value)}
            data-testid="vendedor-selector"
            className="h-11 border border-border rounded-md px-3 bg-card text-sm min-w-[220px]"
          >
            <option value="">Todos los vendedores</option>
            {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name || v.email}</option>)}
          </select>
        )}
      </div>

      {/* KPIs principales por estado — este es el corazón de la vista */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <MetricCard
          label="Comisión total causada"
          value={hidden(total.total_comision)}
          icon={Award}
          tone="primary"
          testid="kpi-comision-total"
          sub={`${total.pedidos} pedido${total.pedidos !== 1 ? "s" : ""}`}
          tooltip="Comisión que has generado sumando todos los pedidos del periodo (pagadas + por cobrar + bloqueadas)."
        />
        <MetricCard
          label="Ya pagadas"
          value={hidden(total.comision_liquidada)}
          icon={CheckCircle2}
          tone="emerald"
          testid="kpi-liquidada"
          sub={`${porEstado.liquidada.pedidos} pedido${porEstado.liquidada.pedidos !== 1 ? "s" : ""}`}
          tooltip="Comisiones que el director ya te pagó (marcadas como liquidadas)."
        />
        <MetricCard
          label="Por comisionar (cliente pagó)"
          value={hidden(total.comision_por_cobrar)}
          icon={Clock}
          tone="amber"
          testid="kpi-por-cobrar"
          sub={`${porEstado.pendiente_liquidacion.pedidos} pedido${porEstado.pendiente_liquidacion.pedidos !== 1 ? "s" : ""}`}
          tooltip="Comisiones listas para cobrarte: el cliente pagó el pedido al 100% y el director aún no te ha liquidado."
        />
        <MetricCard
          label="Bloqueadas por saldo cliente"
          value={hidden(total.comision_bloqueada)}
          icon={Lock}
          tone="rose"
          testid="kpi-bloqueada"
          sub={`${porEstado.pendiente_pago_cliente.pedidos} pedido${porEstado.pendiente_pago_cliente.pedidos !== 1 ? "s" : ""}`}
          tooltip="Comisiones bloqueadas hasta que el cliente termine de pagar el pedido."
        />
      </div>

      {/* Pestañas por estado */}
      <div className="flex flex-wrap gap-2 mb-4">
        {ESTADO_COMISION_TABS.map((tab) => {
          const isActive = estadoComision === tab.k;
          const counts = tab.k === "todos"
            ? { pedidos: total.pedidos, monto: total.total_comision }
            : {
                pedidos: porEstado[tab.k]?.pedidos || 0,
                monto: porEstado[tab.k]?.total_comision || 0,
              };
          const activeColor = {
            muted: "bg-foreground text-background border-foreground",
            emerald: "bg-emerald-600 text-white border-emerald-600",
            amber: "bg-amber-600 text-white border-amber-600",
            rose: "bg-rose-600 text-white border-rose-600",
          }[tab.color];
          const inactiveColor = "bg-card border-border text-foreground hover:bg-accent/50";
          const Icon = tab.icon;
          return (
            <button
              key={tab.k}
              onClick={() => setEstadoComision(tab.k)}
              data-testid={`tab-estado-${tab.k}`}
              className={`px-4 h-11 rounded-full border-2 text-sm font-semibold transition-all flex items-center gap-2 ${isActive ? activeColor : inactiveColor}`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
              <span className={`text-[11px] tabular-nums px-2 py-0.5 rounded-full ${isActive ? "bg-white/25" : "bg-muted"}`}>
                {counts.pedidos}
              </span>
              {tab.k !== "todos" && counts.monto > 0 && (
                <span className={`text-[11px] tabular-nums font-mono ${isActive ? "opacity-90" : "text-muted-foreground"}`}>
                  {hidden(counts.monto)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Explicación del bucket activo (fuera de "todos") */}
      {estadoComision !== "todos" && (
        <div className={`rounded-lg border-l-4 px-4 py-3 mb-4 flex items-start gap-3 ${
          estadoComision === "liquidada" ? "border-emerald-500 bg-emerald-500/5" :
          estadoComision === "pendiente_liquidacion" ? "border-amber-500 bg-amber-500/5" :
          "border-rose-500 bg-rose-500/5"
        }`}>
          <div className="mt-0.5">
            {(() => {
              const Icon = ESTADO_COMISION_META[estadoComision].icon;
              return <Icon className="w-5 h-5" />;
            })()}
          </div>
          <div className="text-sm text-foreground/90 leading-relaxed" data-testid="tab-ayuda">
            {ESTADO_COMISION_META[estadoComision].ayuda}
          </div>
        </div>
      )}

      {/* Tabla agrupada */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2">
          <div className="text-sm font-semibold text-foreground">
            {activeTabMeta ? `Detalle · ${activeTabMeta.label}` : "Detalle por pedido"}
          </div>
          <span className="text-xs text-muted-foreground ml-auto">
            {agrupadoPorPedido.length} pedido{agrupadoPorPedido.length !== 1 ? "s" : ""} · {detalleFiltrado.length} item{detalleFiltrado.length !== 1 ? "s" : ""}
          </span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Cargando…</div>
        ) : agrupadoPorPedido.length === 0 ? (
          <EmptyStateBucket estado={estadoComision} />
        ) : (
          <div className="divide-y divide-border/60">
            {agrupadoPorPedido.map((g) => {
              const meta = ESTADO_COMISION_META[g.estado_comision] || ESTADO_COMISION_META.pendiente_pago_cliente;
              const EstadoIcon = meta.icon;
              return (
                <div key={g.pedido_id} data-testid={`ped-com-${g.pedido_id}`}>
                  <button
                    onClick={() => navigate(`/pedidos?open=${g.pedido_id}`)}
                    className="w-full px-4 py-3 flex items-start gap-3 hover:bg-accent/40 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-foreground">{g.pedido_numero}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border ${meta.color} inline-flex items-center gap-1`}>
                          <EstadoIcon className="w-3 h-3" />
                          {meta.label}
                        </span>
                        <span className="text-xs text-muted-foreground truncate">{g.cliente_nombre}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>{g.fecha}</span>
                        <span>Total pedido: <b className="text-foreground/80">{formatCOP(g.total_pedido)}</b></span>
                        {g.estado_comision === "pendiente_pago_cliente" && g.saldo_pendiente_cliente > 0 && (
                          <span className="text-rose-700">
                            Cliente debe: <b>{formatCOP(g.saldo_pendiente_cliente)}</b>
                          </span>
                        )}
                        {g.estado_comision === "liquidada" && g.liquidada_at && (
                          <span className="text-emerald-700 inline-flex items-center gap-1">
                            <Wallet className="w-3 h-3" />
                            Pagada {(g.liquidada_at || "").slice(0, 10)}
                            {g.metodo_pago_vendedor ? ` · ${g.metodo_pago_vendedor}` : ""}
                            {g.batch_id && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFacturaBatch(g.batch_id);
                                  }}
                                  className="ml-2 underline text-emerald-800 hover:text-emerald-900 text-[10px] font-semibold"
                                  data-testid={`ver-factura-liq-${g.pedido_id}`}
                                  title="Descargar factura con todas las comisiones pagadas en este lote (PDF o imagen)"
                                >
                                  Descargar factura
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    api.get(`/reportes/liquidacion-comisiones/${g.batch_id}/comprobante`, { responseType: "blob" })
                                      .then((res) => {
                                        // Leemos el content-type real del header — si el Blob no lo tiene,
                                        // navegadores como Chrome no saben abrirlo y muestran error.
                                        const type = res.headers?.["content-type"] || "application/octet-stream";
                                        const url = URL.createObjectURL(new Blob([res.data], { type }));
                                        window.open(url, "_blank");
                                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                                      })
                                      .catch((err) => {
                                        const msg = err?.response?.status === 404 ? "Este pago no tiene comprobante adjunto" : "No se pudo abrir";
                                        toast.error(msg);
                                      });
                                  }}
                                  className="ml-2 underline text-emerald-800 hover:text-emerald-900 text-[10px] font-semibold"
                                  data-testid={`ver-comp-liq-${g.pedido_id}`}
                                  title="Ver comprobante del pago (imagen/PDF subido por el director)"
                                >
                                  Ver comprobante
                                </button>
                              </>
                            )}
                          </span>
                        )}
                        {isAdmin && g.vendedor_nombre ? <span>{g.vendedor_nombre}</span> : null}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase font-bold text-muted-foreground">Comisión</div>
                      <div className="text-base font-semibold tabular-nums text-primary" data-testid={`comision-monto-${g.pedido_id}`}>{hidden(g.total_comision)}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                  </button>
                  {/* Sub-items */}
                  <div className="pl-6 pr-4 pb-3 space-y-1 bg-muted/20">
                    {g.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex-1 truncate">
                          {it.cantidad}x {it.producto_nombre}
                          {it.regla_nombre && <span className="text-primary/70"> · {it.regla_nombre}</span>}
                        </span>
                        <span className="tabular-nums w-20 text-right">{formatCOP(it.subtotal)}</span>
                        <span className="tabular-nums w-14 text-right text-primary/80">{it.comision_pct}%</span>
                        <span className="tabular-nums w-24 text-right text-primary font-semibold">{hidden(it.comision_valor)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <DescargaFormatoModal
        open={!!facturaBatch}
        onOpenChange={(v) => { if (!v) setFacturaBatch(null); }}
        titulo="Descargar factura de comisiones"
        onElegir={async (format) => {
          try {
            const params = format === "png" ? { format: "png" } : {};
            const res = await api.get(`/reportes/liquidacion-comisiones/${facturaBatch}/estado-cuenta`, {
              params, responseType: "blob",
            });
            const type = res.headers?.["content-type"] || (format === "png" ? "image/png" : "application/pdf");
            const ext = format === "png" ? "png" : "pdf";
            const url = URL.createObjectURL(new Blob([res.data], { type }));
            const a = document.createElement("a");
            a.href = url; a.download = `Factura-Comisiones-${facturaBatch}.${ext}`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            toast.success("Factura descargada");
            setFacturaBatch(null);
          } catch (e) {
            toast.error("No se pudo descargar");
          }
        }}
      />
    </div>
  );
}

function EmptyStateBucket({ estado }) {
  const msg = {
    todos: {
      title: "Sin comisiones en este rango",
      hint: "Prueba un rango más amplio o crea nuevos pedidos.",
      Icon: Award,
    },
    liquidada: {
      title: "Aún no te han liquidado comisiones",
      hint: "Cuando el director marque tus comisiones como pagadas, aparecerán aquí.",
      Icon: CheckCircle2,
    },
    pendiente_liquidacion: {
      title: "No hay comisiones listas para cobrar",
      hint: "Apenas un cliente termine de pagar un pedido, esa comisión pasará aquí.",
      Icon: Clock,
    },
    pendiente_pago_cliente: {
      title: "Ninguna comisión bloqueada",
      hint: "Todos tus pedidos activos ya fueron pagados por los clientes.",
      Icon: Lock,
    },
  }[estado] || {};
  const Icon = msg.Icon || Award;
  return (
    <div className="p-16 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-muted flex items-center justify-center mb-3">
        <Icon className="w-7 h-7 text-muted-foreground" />
      </div>
      <div className="text-sm font-medium text-foreground">{msg.title}</div>
      <div className="text-xs text-muted-foreground mt-1">{msg.hint}</div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, tone, testid, sub, tooltip }) {
  const tones = {
    primary: "bg-primary/10 border-primary/30 text-primary",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-700",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-800",
    rose: "bg-rose-500/10 border-rose-500/30 text-rose-700",
    blue: "bg-blue-500/10 border-blue-500/30 text-blue-700",
    muted: "bg-muted border-border text-foreground",
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${tones[tone]}`} data-testid={testid} title={tooltip || label}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" aria-label={label} />
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold">{label}</div>
      </div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-wider mt-1 opacity-70">{sub}</div>}
    </div>
  );
}
